import React, { ComponentType } from "react";
import classNames from "classnames";
import { FormFeedback } from "reactstrap";
import { WrappedFieldProps } from "redux-form";

import { FormGroup, FormGroupProps } from "@ui/Form/FormGroup";
import { ModelSelectOuterProps } from "../ModelSelect";

interface FormModelSelectProps<M> extends ModelSelectOuterProps<M> {
    componentClass: ComponentType<ModelSelectOuterProps<M>>;
    getOptionValue: (m: M) => string;
    name: string;
    label: string;
    disabled?: boolean;
    showLabel?: boolean;
    onModelChange?: (m: M | M[]) => void;
}

type Props<M> = WrappedFieldProps & FormModelSelectProps<M>;

const FormModelSelect = <M, >(props: Props<M>): JSX.Element => {
    const {
        componentClass: Component,
        getOptionValue,
        name,
        label,
        showLabel,
        input,
        meta: { error, touched },
        ...selectProps
    } = props;

    // Sort the group props
    const hasError = touched && error != null;
    const groupProps: FormGroupProps = {
        label,
        hasError,
        showLabel,
        htmlFor: selectProps.id,
    };
    // And the selectProps
    const { onModelChange } = selectProps;
    selectProps.onChange = (m: M | M[]) => {
        if (Array.isArray(m)) {
            input.onChange(m.map(getOptionValue));
        }
        else {
            input.onChange(getOptionValue(m));
        }
        if (onModelChange != null) {
            onModelChange(m);
        }
    };
    selectProps.selected = input?.value === "" ? null : input?.value;

    return (
        <FormGroup {...groupProps}>
            <div className={classNames({
                    "form-react-select": true,
                    "is-invalid": hasError,
                })}
            >
                <Component {...selectProps} />
            </div>
            {hasError && <FormFeedback>{error}</FormFeedback>}
        </FormGroup>
    );
};

export {
    FormModelSelect,
    Props as FormModelSelectProps,
    FormModelSelectProps as FormModelSelectOwnProps,
};
